import React, { useMemo } from 'react';
import { useTranslate } from 'gatsby-theme-soluble-source';
import { graphql } from 'gatsby';

import FooterDS from 'design-system/src/components/molecules/website/Footer/Footer';
import FooterNavigation from 'design-system/src/components/molecules/website/Footer/Navigation';
import PreFooterContact from 'design-system/src/components/organisms/website/PreFooterContact';

interface NavItem {
    childLinks: NavChildItem[];
}
interface NavChildItem extends NavItem {
    label: string;
    slug: string;
}
interface FooterProps {
    navs: NavItem[];
    [key: string]: any;
}

const Footer: React.FunctionComponent<FooterProps> = function Footer({
    data,
    layoutProps,
    ...props
}) {
    const _ = useTranslate();

    const {
        links,
        social,
        navigationItems: { nodes: navigation },
    } = data;

    const [footerLinks, socialLinks] = useMemo(
        () =>
            [links, social].map((nav) => {
                return (
                    nav?.childLinks
                        .sort((a, b) => a.position - b.position)
                        .map((item) => {
                            return {
                                label: item.label,
                                href: item.href,
                            };
                        }) ?? []
                );
            }),
        [social, links],
    );

    const socialIcons = useMemo(
        () =>
            socialLinks
                ? socialLinks.map((item) => {
                      return {
                          url: item.href,
                          name: item.label.toLowerCase(),
                          label: item.label,
                      };
                  })
                : [],

        [socialLinks],
    );

    const navigationLinks = useMemo(
        () =>
            navigation
                .map((column) => {
                    const childLinks =
                        column.childLinks?.[0]?.childLinks?.sort(
                            (a, b) => a.position - b.position,
                        ) ?? [];

                    return {
                        links: [...column.childLinks, ...childLinks].map(
                            (link) => {
                                return {
                                    label: link.label,
                                    href: link.href,
                                };
                            },
                        ),
                    };
                })
                .filter((column) => column?.links?.length > 0),
        [navigation],
    );

    return (
        <>
            {!layoutProps.hidePreFooter && (
                <PreFooterContact
                    title={_['prefooter.title'].text}
                    information={_['prefooter.text'].html}
                    titleTag="h4"
                />
            )}
            <FooterNavigation navs={navigationLinks} />
            <FooterDS
                variant="dark"
                links={footerLinks}
                socialIcons={socialIcons}
                copyright={_['footer.copyright']?.text}
                {...props}
            />
        </>
    );
};

export default Footer;

export const query = graphql`
    fragment FooterLink on DatoCmsFooter {
        label
        href
    }
    query Footer($language: String) {
        links: datoCmsFooter(
            locale: { eq: $language }
            label: { eq: "_links" }
        ) {
            ...FooterLink
            childLinks: treeChildren {
                position
                ...FooterLink
            }
        }
        social: datoCmsFooter(
            locale: { eq: $language }
            label: { eq: "_social" }
        ) {
            ...FooterLink
            childLinks: treeChildren {
                position
                ...FooterLink
            }
        }
        navigationItems: allDatoCmsFooter(
            filter: {
                locale: { eq: $language }
                label: { in: ["_1", "_2", "_3", "_4"] }
            }
            sort: { position: ASC }
        ) {
            nodes {
                ...FooterLink
                childLinks: treeChildren {
                    ...FooterLink
                    childLinks: treeChildren {
                        ...FooterLink
                        position
                    }
                }
            }
        }
    }
`;
